// Minimal hash-based history sync — no router library. The SPA's "view"
// (top-level tab, optional selected record) is mirrored 1:1 into
// `location.hash` so the browser Back/Forward buttons walk through app
// views instead of leaving the site.
//
// Route map:
//   #/overview                admin: Projects overview
//   #/inbox                   admin: Ticket inbox
//   #/deploys                 admin: Deploys
//   #/daily/<YYYY-MM-DD>      admin: Daily activity ("who did what that day")
//   #/users                   admin: Users & access
//   #/project/<id>            project workspace (board/tickets/debt/docs/
//                              activity tabs)
//   #/ticket/<id>              ticket detail
//   #/doc/<id>                 doc reader
//   #/settings
//
// Convention used everywhere this module is consumed: user-triggered
// navigation (nav click, opening a ticket, back-to-list) calls `push`, one
// history entry per view change. Programmatic redirects (post-login landing,
// logout, bouncing off an inaccessible id) call `replace` so Back can't get
// trapped in a loop.
//
// `back` on the ticket/doc routes is an in-memory-only convenience (not
// encoded in the hash, same as the prototype's `route.back`) so those pages'
// own Back button returns to wherever the user actually came from instead of
// a fixed parent — it just doesn't survive a hard reload, which is fine.

import { istDay, isValidDay } from '$lib/utils/format';

export type TabId =
  | 'overview'
  | 'inbox'
  | 'deploys'
  | 'daily'
  | 'users'
  | 'project'
  | 'ticket'
  | 'doc'
  | 'settings';

export type Route =
  | { tab: 'overview' }
  | { tab: 'inbox' }
  | { tab: 'deploys' }
  // The selected day lives in the hash (not component state) so a refresh
  // or a shared link keeps the day the viewer was looking at.
  | { tab: 'daily'; date: string }
  | { tab: 'users' }
  | { tab: 'project'; projectId: number }
  | { tab: 'ticket'; ticketId: number; back?: Route }
  | { tab: 'doc'; docId: number; back?: Route }
  | { tab: 'settings' };

function toHash(r: Route): string {
  if (r.tab === 'daily') return `#/daily/${r.date}`;
  if (r.tab === 'project') return `#/project/${r.projectId}`;
  if (r.tab === 'ticket') return `#/ticket/${r.ticketId}`;
  if (r.tab === 'doc') return `#/doc/${r.docId}`;
  return `#/${r.tab}`;
}

function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  const parts = raw.split('/').filter(Boolean);
  const [tab, sub] = parts;
  if (tab === 'project') {
    const id = Number(sub);
    if (Number.isFinite(id)) return { tab: 'project', projectId: id };
    return { tab: 'overview' };
  }
  if (tab === 'ticket') {
    const id = Number(sub);
    if (Number.isFinite(id)) return { tab: 'ticket', ticketId: id };
    return { tab: 'overview' };
  }
  if (tab === 'doc') {
    const id = Number(sub);
    if (Number.isFinite(id)) return { tab: 'doc', docId: id };
    return { tab: 'overview' };
  }
  // A missing/garbled day falls back to today rather than bouncing to
  // overview — the tab is still perfectly valid, only the day isn't.
  if (tab === 'daily') {
    const day = sub ?? '';
    return { tab: 'daily', date: isValidDay(day) ? day : istDay() };
  }
  if (tab === 'inbox' || tab === 'deploys' || tab === 'users' || tab === 'settings') return { tab };
  return { tab: 'overview' };
}

class RouteState {
  current = $state<Route>(parseHash(location.hash));

  // User-initiated navigation — one new history entry per view change.
  push(r: Route) {
    this.current = r;
    const hash = toHash(r);
    if (location.hash !== hash) history.pushState(null, '', hash);
  }

  // Programmatic redirects (post-login landing, logout, invalid/stale
  // routes) — replaces the current entry so Back can't loop onto a dead view.
  replace(r: Route) {
    this.current = r;
    const hash = toHash(r);
    if (location.hash !== hash) history.replaceState(null, '', hash);
  }
}

export const route = new RouteState();

// Browser Back/Forward (popstate) and any direct hash edits (hashchange) —
// re-parse from location.hash rather than trusting the event, since both
// fire for the same "the fragment changed under us" case.
function syncFromLocation() {
  route.current = parseHash(location.hash);
}
window.addEventListener('popstate', syncFromLocation);
window.addEventListener('hashchange', syncFromLocation);
